//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import MobileDeviceId from '@models/MobileDeviceId';
import UserId from '@models/UserId';
import { UniversalCalendarView } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { WeekDayValue } from '@utils/moment';

export interface IAlertDismissStatus {
  isDismissed: boolean;
  lastTimeSeen: number;
  firstViewTimestamp: number;
}

export default interface IUserSettings {
  defaultPublishingStrategy?: LegacyInstagramPublishingStrategy;
  /**
   * THIS FIELD IS DEPRECATED, DO NOT USE!
   * https://curalate.atlassian.net/browse/DEV-50181
   */
  defaultMobileDevice?: ISchedulerDefaultMobileDevice;
  defaultPostingTime?: ISchedulerDefaultPostingTime;
  defaultNotificationUserId?: ISchedulerDefaultNotificationUserId;
  autoPostPermissionModalDismissed?: boolean;
  isShareOnboardingComplete?: boolean;
  contentAcquisition?: IContentAcquisitionUserSettings;
  contentDiscoveryFirstRunStatus?: IContentDiscoveryFirstRunStatus;
  skipInstagramPostManualPostingInstructions?: boolean;
  galleriesPinnedMediaCannotPinAlert?: Partial<IAlertDismissStatus>;
  galleriesPinnedMediaDragAndDropAlert?: Partial<IAlertDismissStatus>;
  hasViewedVibeOnboarding?: boolean;
  optinToVisualRefresh2020?: boolean;
  isInsightsRefreshOptedOut?: boolean;
  isInsightsRefreshOptOutBannerDismissed?: boolean;
  scheduleCalendarStartOfWeek?: WeekDayValue;
  scheduleCalendarView?: UniversalCalendarView;
}

/**
 * This capitalized enum was used to regulate publishing strategies throughout
 * the old instagram-post-scheduler code. The only place it remains in use is
 * in user settings.
 */
export enum LegacyInstagramPublishingStrategy {
  Automatic = 'Automatic',
  Manual = 'Manual',
}

/**
 * This field is split between device ids and user ids because of a mixup
 * when user_based_scheduling was released. It is now being deprecated in
 * favor of the defaultNotificationUserId field and will be removed here:
 * https://curalate.atlassian.net/browse/DEV-50181
 */
export interface ISchedulerDefaultMobileDevice {
  mobileDeviceId: MobileDeviceId | UserId;
}

/**
 * The userId here is optional because a default notification user
 * is cleared by setting it to be undefined.
 */
export interface ISchedulerDefaultNotificationUserId {
  userId?: UserId;
}

/**
 * Both fields on the default are optional because a default posting
 * time is cleared by setting both the minute and hour values to undefined.
 */
export interface ISchedulerDefaultPostingTime {
  hour?: number;
  minute?: number;
}

export interface IContentAcquisitionUserSettings {
  productionCost: number;
  acquisitionType: string;
}

export interface IContentDiscoveryFirstRunStatus {
  firstViewTimestamp?: number;
  isClosed?: boolean;
}
